import * as XLSX from 'xlsx';
import {
  extractNormalizedHeaders,
  getColumnIndexByName,
  normalize,
  findMissingColumns
} from './excelUtils';


export const readWorkbookFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const abuf = e.target?.result;
      if (!abuf) return reject(new Error('Empty file buffer'));
      try {
        const workbook = XLSX.read(abuf, { type: 'array' });
        resolve(workbook);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

// Resolves the required column indices for a sheet, throws if structure is invalid
function getRequiredColumnIndices(worksheet, sheetName, columnMapping) {
  if (!worksheet || !worksheet['!ref']) {
    throw new Error(`Sheet "${sheetName}" is empty.`);
  }

  const headers = extractNormalizedHeaders(worksheet);
  const requiredColumns = Object.values(columnMapping);
  const missingColumns = findMissingColumns(headers, requiredColumns);

  if (missingColumns.length > 0) {
    throw new Error(`Missing required column(s) in sheet "${sheetName}": ${missingColumns.join(', ')}`);
  }

  const smzHeader = columnMapping.siteManagerZone;
  const smzColIndex = getColumnIndexByName(headers, smzHeader);
  if (smzColIndex === -1) {
    throw new Error(`SMZ column "${smzHeader}" not found in sheet "${sheetName}".`);
  }

  const linguisticRegimeHeader = columnMapping.linguisticRegime;
  const linguisticRegimeColIndex = getColumnIndexByName(headers, linguisticRegimeHeader);
  if (linguisticRegimeColIndex === -1) {
    throw new Error(`Linguistic Regime column "${linguisticRegimeHeader}" not found in sheet "${sheetName}".`);
  }

  const pageSizeHeader = columnMapping.pageSize;
  const pageSizeColIndex = getColumnIndexByName(headers, pageSizeHeader);
  if (pageSizeColIndex === -1) {
    throw new Error(`Page Size column "${pageSizeHeader}" not found in sheet "${sheetName}".`);
  }

  return { smzColIndex, linguisticRegimeColIndex, pageSizeColIndex };
}

// Validates the SMZ / linguistic regime / page size values of every data row of a sheet, throws on the first invalid row
function validateSheetRows(worksheet, sheetName, indices, allowedLinguisticRegimes, allowedPageSizes) {
  const { smzColIndex, linguisticRegimeColIndex, pageSizeColIndex } = indices;
  const range = XLSX.utils.decode_range(worksheet['!ref']);

  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const smzCellAddress = XLSX.utils.encode_cell({ r: row, c: smzColIndex });
    const lingRegCellAddress = XLSX.utils.encode_cell({ r: row, c: linguisticRegimeColIndex });
    const pageSizeCellAddress = XLSX.utils.encode_cell({ r: row, c: pageSizeColIndex });

    const smzRawValue = worksheet[smzCellAddress]?.v;
    const lingRegRawValue = worksheet[lingRegCellAddress]?.v;
    const pageSizeRawValue = worksheet[pageSizeCellAddress]?.v;

    const smzValue = normalize(smzRawValue || "").trim();
    const lingRegValue = normalize(lingRegRawValue || "").trim().toUpperCase();
    const pageSizeValue = (pageSizeRawValue || "").toString().trim();

    // Skip row if SMZ and linguistic regime are both empty
    if (!smzValue && !lingRegValue) {
      continue;
    }

    if (!smzValue) {
      throw new Error(`Empty "SMZ Name" in sheet "${sheetName}" at row ${row + 1}`);
    }
    if (!lingRegValue) {
      throw new Error(`Empty Linguistic Regime in sheet "${sheetName}" at row ${row + 1}`);
    }
    if (!allowedLinguisticRegimes.includes(lingRegValue)) {
      throw new Error(`Invalid Linguistic Regime "${lingRegRawValue}" in sheet "${sheetName}" at row ${row + 1}`);
    }
    if (!pageSizeValue) {
      throw new Error(`Empty Page Size in sheet "${sheetName}" at row ${row + 1}`);
    }
    if (!allowedPageSizes.includes(pageSizeValue)) {
      throw new Error(`Invalid Page Size "${pageSizeValue}" in sheet "${sheetName}" at row ${row + 1}. Allowed values: ${allowedPageSizes.join(', ')}`);
    }
  }
}

export const validateWorkbook = (workbook, columnMapping) => {
  return new Promise((resolve, reject) => {
    try {
      const sheetNamesInFile = workbook.SheetNames;
      const requiredSheets = ['Spaces', 'Assets'];
      const missingSheets = requiredSheets.filter(
        (sheet) => !sheetNamesInFile.includes(sheet)
      );

      if (missingSheets.length > 0) {
        throw new Error(`Invalid Excel: Missing required sheet(s): ${missingSheets.join(', ')}`);
      }

      const allowedLinguisticRegimes = ['NL/FR', 'FR/NL', 'NL', 'FR', 'FR/DE'];
      const allowedPageSizes = ['A6', 'A7'];

      for (const sheetName of requiredSheets) {
        const worksheet = workbook.Sheets[sheetName];
        const indices = getRequiredColumnIndices(worksheet, sheetName, columnMapping);
        validateSheetRows(worksheet, sheetName, indices, allowedLinguisticRegimes, allowedPageSizes);
      }

      resolve(null); // ✅ Workbook is valid

    } catch (error) {
      console.error('Error in validateWorkbook:', error);
      reject(error);
    }
  });
};


export const readRequestsFromWorkbook = (workbook, sheetName, columnMapping) => {
  return new Promise((resolve, reject) => {
    try {
      const worksheet = workbook.Sheets[sheetName];
      if (!worksheet || !worksheet['!ref']) return resolve([]);

      const range = XLSX.utils.decode_range(worksheet['!ref']);
      const headers = extractNormalizedHeaders(worksheet);
      const { data, skippedRows } = parseDataRows(
        worksheet,
        range,
        headers,
        columnMapping,
        sheetName,
        workbook
      );

      resolve({
        records: data,
        skipped: skippedRows,
        totalCount: data.length + skippedRows.length,
        successCount: data.length,
        skippedCount: skippedRows.length
      });
    } catch (error) {
      reject(error);
    }
  });
};



function isRowCompletelyEmpty(worksheet, row, range) {
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
    const cell = worksheet[cellAddress];
    if (cell && cell.v !== undefined && cell.v !== null && `${cell.v}`.trim() !== '') {
      return false; // At least one non-empty cell found
    }
  }
  return true;
}

function parseDataRows(worksheet, range, headers, columnMapping, sheetName, workbook) {
  const data = [];
  const skippedRows = [];
  const headerRow = range.s.r;

  const sheetIndex = workbook.SheetNames.indexOf(sheetName);

  for (let row = headerRow + 1; row <= range.e.r; row++) {
    // ✅ Skip entirely empty rows
    if (isRowCompletelyEmpty(worksheet, row, range)) {
      continue;
    }

    const { rowData, errorReason, errorColumn } = parseRow(
      worksheet,
      row,
      headers,
      columnMapping
    );

    if (rowData) {
      data.push({
        ...rowData,
        _sheetIndex: sheetIndex,
        _rowIndex: row, // raw Excel row number
      });
    } else {
      skippedRows.push({
        tabName: sheetName,
        columnName: errorColumn || "Unknown",
        rowNumber: row + 1,
        reason: errorReason || "Unknown error",
      });
    }
  }

  return { data, skippedRows };
}



// Reads and normalizes the cell values of a row into an object keyed by columnMapping keys
function buildRowObject(worksheet, row, headers, columnMapping) {
  const obj = {};
  let hasValue = false;

  for (const [key, columnName] of Object.entries(columnMapping)) {
    const colIdx = getColumnIndexByName(headers, columnName);
    if (colIdx !== -1) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIdx });
      const cellValue = worksheet[cellAddress]?.v ?? '';
      const normalizedValue = normalize(cellValue);
      obj[key] = normalizedValue;

      if (normalizedValue !== '') {
        hasValue = true;
      }
    }
  }

  return { obj, hasValue };
}

function isLabelEmpty(value) {
  // Treat both 0 and "0" as valid, anything else empty as invalid
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "") ||
    (typeof value === "number" && Number.isNaN(value))
  );
}

function validateLabels(obj, columnMapping) {
  const labelFields = ['label1', 'label2', 'label3'];
  for (const labelKey of labelFields) {
    if (isLabelEmpty(obj[labelKey])) {
      return { errorReason: "Missing value", errorColumn: columnMapping[labelKey] || labelKey };
    }
  }
  return null;
}

const ALLOWED_URL_SUBSTRINGS = [
  'https://fixmystation.dev-belgiantrain.be/link/fms/facility-space/',
  'https://fixmystation.dev-belgiantrain.be/link/fms/facility-asset/',
  'https://fixmystation.tst-belgiantrain.be/link/fms/facility-space/',
  'https://fixmystation.tst-belgiantrain.be/link/fms/facility-asset/',
  'https://fixmystation.acc-belgiantrain.be/link/fms/facility-space/',
  'https://fixmystation.acc-belgiantrain.be/link/fms/facility-asset/',
  'https://fixmystation.belgiantrain.be/link/fms/facility-space/',
  'https://fixmystation.belgiantrain.be/link/fms/facility-asset/',
];
const ALLOWED_ORIENTATIONS = ['Landscape', 'Portrait'];
const ALLOWED_PAGE_SIZES = ['A6', 'A7'];
const ALLOWED_LINGUISTIC_REGIMES = ['NL/FR', 'FR/NL', 'NL', 'FR', 'FR/DE'];

function validateRowValues(obj, columnMapping, hasValue) {
  if (!hasValue) {
    return { errorReason: 'Empty or invalid row (all required fields are blank)', errorColumn: 'ALL' };
  }
  if (!obj.copies || obj.copies === 0 || obj.copies === '') {
    return { errorReason: 'Number of stickers is 0 or blank', errorColumn: columnMapping.copies };
  }
  if (!obj.url) {
    return { errorReason: 'Missing URL', errorColumn: columnMapping.url };
  }
  if (!ALLOWED_URL_SUBSTRINGS.some(sub => obj.url.includes(sub))) {
    return { errorReason: 'Invalid URL', errorColumn: columnMapping.url };
  }
  if (obj.orientation === 0 || obj.orientation === '' || obj.orientation === null || obj.orientation === undefined) {
    return { errorReason: 'Orientation is 0 or blank', errorColumn: columnMapping.orientation };
  }
  if (obj.orientation && !ALLOWED_ORIENTATIONS.includes(obj.orientation)) {
    return { errorReason: 'Invalid Orientation (must be Landscape or Portrait)', errorColumn: columnMapping.orientation };
  }
  if (obj.pageSize && !ALLOWED_PAGE_SIZES.includes(obj.pageSize)) {
    return { errorReason: 'Invalid Page Size (must be A6 or A7)', errorColumn: columnMapping.pageSize };
  }
  if (obj.pageSize === 'A7' && obj.orientation?.toLowerCase() === 'landscape') {
    return { errorReason: 'Invalid: A7 page size cannot have Landscape orientation', errorColumn: `${columnMapping.pageSize} / ${columnMapping.orientation}` };
  }
  if (obj.linguisticRegime && !ALLOWED_LINGUISTIC_REGIMES.includes(obj.linguisticRegime.toUpperCase())) {
    return { errorReason: 'Invalid Linguistic Regime', errorColumn: columnMapping.linguisticRegime };
  }
  return null;
}

function parseRow(worksheet, row, headers, columnMapping) {
  const { obj, hasValue } = buildRowObject(worksheet, row, headers, columnMapping);

  const validationError = validateLabels(obj, columnMapping) ?? validateRowValues(obj, columnMapping, hasValue);

  return {
    rowData: !validationError ? obj : null,
    errorReason: validationError?.errorReason ?? null,
    errorColumn: validationError?.errorColumn ?? null
  };
}

